import { createSlice } from '@reduxjs/toolkit';
import {login, logout} from "../../common/account/accountSlice";
import config from "../../config";

// When false, Explore is a single-product experience (Contractor only), even if
// the organization has additional products configured.
const MULTI_PRODUCT_ENABLED = !!config?.features?.multiProduct;

// A product is available unless explicitly unsubscribed. Soft gate: legacy
// subscriptions without the `subscribed` flag remain accessible; only an explicit
// `subscribed: false` hides the product.
const isSubscribed = (product) => !!product && product.subscribed !== false;

const initialState = {
    currentUniverse: 'BUSINESS', // BUSINESS | CONTRACTOR | ...
    universes: {
        BUSINESS: {
            entities: {},    // Keep Filter of Business
            activeCount: 0,
            searchQuery: '', // Search Bar of Business
            subscription: {},
            toggleMap: true,
            exploreMode: 'MAP', // [MAP, LIST]
            exploreByContact: false,
            accuracyData: null,
        },
        CONTRACTOR: {
            entities: {},    // Keep Filter of Contractor
            activeCount: 0,
            searchQuery: '',
            subscription: {},
            toggleMap: true,
            exploreMode: 'MAP', // [MAP, LIST]
            exploreByContact: false,
            accuracyData: null,
        },
    },
    // Global Folder State
    folderState: {}
};

const universeSlice = createSlice({
    name: 'universe',
    initialState,
    reducers: {
        switchUniverse: (state, action) => {
            state.currentUniverse = action.payload; // 'BUSINESS', 'CONTRACTOR'
        },

        setToggleMap: (state, action) => {
            const universeKey = state.currentUniverse;
            state.universes[universeKey].toggleMap = action.payload;
        },

        setExploreByContact: (state, action) => {
            const universeKey = state.currentUniverse;
            state.universes[universeKey].exploreByContact = action.payload;
        },

        setAccuracyData: (state, action) => {
            const universeKey = state.currentUniverse;
            if (universeKey && state.universes[universeKey]) {
                state.universes[universeKey].accuracyData = action.payload;
            }
        },

        setFilterValue: (state, action) => {
            let { id, value } = action.payload;

            const universeKey = state.currentUniverse;
            const targetUniverse = state.universes[universeKey];

            if (value === '' || value === null || value === undefined) {
                delete targetUniverse.entities[id];
            } else {
                targetUniverse.entities[id] = action.payload;
            }

            // Update Count
            targetUniverse.activeCount = Object.keys(targetUniverse.entities).length;
        },

        removeFilter: (state, action) => {
            const filterId = action.payload;
            const universeKey = state.currentUniverse;
            const targetUniverse = state.universes[universeKey];

            if (targetUniverse && targetUniverse.entities[filterId]) {
                delete targetUniverse.entities[filterId];
                targetUniverse.activeCount = Object.keys(targetUniverse.entities).length;
            }
        },

        clearAllFilters: (state) => {
            const universeKey = state.currentUniverse;
            const targetUniverse = state.universes[universeKey];

            // Clear data
            targetUniverse.entities = {};
            targetUniverse.activeCount = 0;
            targetUniverse.searchQuery = '';
        },

        replaceFilters: (state, action) => {
            const universeKey = state.currentUniverse;
            const targetUniverse = state.universes[universeKey];
            const newEntities = {};

            const filters = Array.isArray(action.payload) ? action.payload : [];

            filters.forEach(f => {
                newEntities[f.id] = f;
            });
            targetUniverse.entities = newEntities;
            targetUniverse.activeCount = Object.keys(newEntities).length;
        },

        clearCurrentUniverseFilters: (state) => {
            const universeKey = state.currentUniverse;
            state.universes[universeKey].entities = {};
            state.universes[universeKey].activeCount = 0;
            state.universes[universeKey].searchQuery = '';
        },

        setSearchQuery: (state, action) => {
            const universeKey = state.currentUniverse;
            state.universes[universeKey].searchQuery = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(login.fulfilled, (state, action) => {
            const account = action.payload;
            const subscription = account?.organization?.subscription;

            Object.keys(state.universes).forEach((universeKey) => {
                state.universes[universeKey].entities = {};
                state.universes[universeKey].activeCount = 0;
                state.universes[universeKey].searchQuery = '';
                state.universes[universeKey].exploreByContact = false;
                state.universes[universeKey].toggleMap = true;
                state.universes[universeKey].exploreMode = 'MAP';
                state.universes[universeKey].accuracyData = null;
            });

            // Populate every subscription the account has access to, independently.
            // Using if/else if here previously left the second universe with an empty
            // subscription object, which made switching to it fail the explore-enabled
            // check in ExploreContainer and surfaced the ScreenLockOverlay.
            // Single-product mode: only the Contractor Universe is populated and
            // accessible, regardless of what else the organization has configured.
            if (!MULTI_PRODUCT_ENABLED) {
                if (isSubscribed(subscription?.contractor_universe)) {
                    state.universes['CONTRACTOR'].subscription = subscription.contractor_universe;
                    state.currentUniverse = 'CONTRACTOR';
                } else {
                    state.currentUniverse = null;
                }
                return;
            }

            // Only populate products the org is subscribed to.
            if (isSubscribed(subscription?.business_universe)) {
                state.universes['BUSINESS'].subscription = subscription.business_universe;
            }
            if (isSubscribed(subscription?.contractor_universe)) {
                state.universes['CONTRACTOR'].subscription = subscription.contractor_universe;
            }

            // Use the admin-configured default flag (must also be subscribed);
            // fall back to Contractor, then Business — likewise subscribed.
            const PRODUCT_UNIVERSE_MAP = {
                contractor_universe: 'CONTRACTOR',
                business_universe: 'BUSINESS',
            };
            const defaultEntry = Object.entries(subscription || {})
                .find(([, val]) => val?.default === true && isSubscribed(val));
            const defaultUniverseKey = defaultEntry ? PRODUCT_UNIVERSE_MAP[defaultEntry[0]] : null;

            if (defaultUniverseKey && state.universes[defaultUniverseKey]) {
                state.currentUniverse = defaultUniverseKey;
            } else if (isSubscribed(subscription?.contractor_universe)) {
                state.currentUniverse = 'CONTRACTOR';
            } else if (isSubscribed(subscription?.business_universe)) {
                state.currentUniverse = 'BUSINESS';
            } else {
                state.currentUniverse = null;
            }
        });

        builder.addCase(logout.fulfilled, (state) => {
            return initialState;
        });
    }
});

export const {
    switchUniverse,
    setToggleMap,
    setExploreByContact,
    setAccuracyData,
    setFilterValue,
    removeFilter,
    clearCurrentUniverseFilters,
    setSearchQuery,
    clearAllFilters,
    replaceFilters,
} = universeSlice.actions;

export default universeSlice.reducer;